const PRINT_THUMBNAIL_EDGE = 256;
const PRINT_THUMBNAIL_QUALITY = 0.72;
const PRINT_THUMBNAIL_SELECTOR = 'img[data-print-thumbnail="true"]';
const PRINT_THUMBNAIL_TIMEOUT_MS = 3_000;
const PRINT_THUMBNAIL_CONCURRENCY = 4;

interface OriginalImageAttributes {
  image: HTMLImageElement;
  src: string | null;
  srcset: string | null;
  sizes: string | null;
}

function restoreAttribute(element: HTMLImageElement, name: string, value: string | null): void {
  if (value === null) element.removeAttribute(name);
  else element.setAttribute(name, value);
}

async function createPrintThumbnail(source: string): Promise<string | null> {
  if (typeof createImageBitmap !== 'function') return null;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), PRINT_THUMBNAIL_TIMEOUT_MS);

  try {
    const response = await fetch(source, { signal: controller.signal });
    if (!response.ok) return null;

    const image = await createImageBitmap(await response.blob());
    try {
      const scale = Math.min(1, PRINT_THUMBNAIL_EDGE / Math.max(image.width, image.height));
      if (scale === 1) return null;

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      if (!context) return null;

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', PRINT_THUMBNAIL_QUALITY),
      );
      return blob?.type === 'image/jpeg' ? URL.createObjectURL(blob) : null;
    } finally {
      image.close();
    }
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function preparePrintThumbnails(): Promise<() => void> {
  const originals: OriginalImageAttributes[] = [];
  const imageEntries = Array.from(
    document.querySelectorAll<HTMLImageElement>(PRINT_THUMBNAIL_SELECTOR),
    (image) => ({ image, source: image.currentSrc || image.src }),
  ).filter(({ source }) => source.length > 0);
  const sources = [...new Set(imageEntries.map(({ source }) => source))];
  const assets = new Map<string, string>();

  for (let index = 0; index < sources.length; index += PRINT_THUMBNAIL_CONCURRENCY) {
    const batch = await Promise.all(
      sources.slice(index, index + PRINT_THUMBNAIL_CONCURRENCY).map(async (source) => {
        const optimizedSource = await createPrintThumbnail(source).catch(() => null);
        return [source, optimizedSource] as const;
      }),
    );
    for (const [source, optimizedSource] of batch) {
      if (optimizedSource) assets.set(source, optimizedSource);
    }
  }

  await Promise.all(
    imageEntries.map(async ({ image, source }) => {
      const optimizedSource = assets.get(source);
      if (!optimizedSource) return;

      const original = {
        image,
        src: image.getAttribute('src'),
        srcset: image.getAttribute('srcset'),
        sizes: image.getAttribute('sizes'),
      };
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = optimizedSource;
      try {
        await image.decode();
        originals.push(original);
      } catch {
        restoreAttribute(image, 'src', original.src);
        restoreAttribute(image, 'srcset', original.srcset);
        restoreAttribute(image, 'sizes', original.sizes);
      }
    }),
  );

  const objectUrls = [...new Set(assets.values())];

  return () => {
    for (const original of originals) {
      restoreAttribute(original.image, 'src', original.src);
      restoreAttribute(original.image, 'srcset', original.srcset);
      restoreAttribute(original.image, 'sizes', original.sizes);
    }
    for (const url of objectUrls) URL.revokeObjectURL(url);
  };
}
